import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CapacidadeQuery,
  DiaCalendario,
  DiaCalendarioAtualizacao,
  EntidadeLote,
  FaixaCapacidade,
  GeracaoCalendario,
  LarguraMae,
  Maquina,
  MaquinaAtualizacao,
  MetodoLote,
  RegraSetup,
  ResultadoGeracao,
  RetornoLinha,
  TipoTroca,
  Turno,
  TurnoAtualizacao,
} from '@pcp/contracts';
import { CHAVE_LOTE } from '@pcp/contracts';
import { api } from './client';

/*
 * Cadastros (M1). Rotas e campos do contrato (docs/04): nomes do Progress, em português e com hífen.
 * Escritas em lote devolvem uma linha de retorno por linha enviada, na mesma ordem:
 * `{ situacao: 'OK'|'ERRO', errornumber, errordescription }`; conflito de versão = errornumber 409.
 */

export const cadastroKeys = {
  machines: ['maquinas'] as const,
  shifts: ['turnos'] as const,
  capacity: (q: CapacidadeQuery) => ['capacidade', q] as const,
  motherWidths: ['largura-mae'] as const,
  changeoverTypes: ['tipo-troca'] as const,
  setup: ['setup'] as const,
  calendar: (q: { maquina?: string; de: string; ate: string }) => ['calendario', q] as const,
};

/** O que depende dos cadastros e precisa recalcular: os planos e as cargas. */
const AFFECTS_PLANS = ['planos'] as const;

const LIST_KEYS: Record<EntidadeLote, readonly unknown[]> = {
  capacidade: ['capacidade'],
  'largura-mae': cadastroKeys.motherWidths,
  'tipo-troca': cadastroKeys.changeoverTypes,
  setup: cadastroKeys.setup,
};

export const CONFLICT = 409;
export const isConflict = (r: RetornoLinha) => r.situacao === 'ERRO' && r.errornumber === CONFLICT;
export const isOk = (r: RetornoLinha) => r.situacao === 'OK';

// ---------- Leituras ----------

export function useMaquinas() {
  return useQuery({
    queryKey: cadastroKeys.machines,
    queryFn: ({ signal }) => api<Maquina[]>('/maquinas', { signal }),
  });
}

export function useTurnos() {
  return useQuery({
    queryKey: cadastroKeys.shifts,
    queryFn: ({ signal }) => api<Turno[]>('/turnos', { signal }),
  });
}

export function useCapacidade(query: CapacidadeQuery) {
  return useQuery({
    queryKey: cadastroKeys.capacity(query),
    queryFn: ({ signal }) => api<FaixaCapacidade[]>('/capacidade', { query, signal }),
  });
}

export function useLarguraMae() {
  return useQuery({
    queryKey: cadastroKeys.motherWidths,
    queryFn: ({ signal }) => api<LarguraMae[]>('/largura-mae', { signal }),
  });
}

export function useTiposTroca() {
  return useQuery({
    queryKey: cadastroKeys.changeoverTypes,
    queryFn: ({ signal }) => api<TipoTroca[]>('/tipo-troca', { signal }),
  });
}

export function useSetup() {
  return useQuery({
    queryKey: cadastroKeys.setup,
    queryFn: ({ signal }) => api<RegraSetup[]>('/setup', { signal }),
  });
}

export function useCalendario(q: { maquina?: string; de: string; ate: string }) {
  return useQuery({
    queryKey: cadastroKeys.calendar(q),
    queryFn: ({ signal }) => api<DiaCalendario[]>('/calendario', { query: q, signal }),
  });
}

// ---------- Escritas ----------

function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: (readonly unknown[])[]) =>
    Promise.all([
      ...keys.map((queryKey) => qc.invalidateQueries({ queryKey })),
      qc.invalidateQueries({ queryKey: AFFECTS_PLANS }),
    ]);
}

/** `PUT /maquinas`: lote parcial. Cada linha leva a chave, os campos alterados e o `version` lido antes. */
export function useUpdateMaquinas() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (maquinas: MaquinaAtualizacao[]) =>
      api<RetornoLinha[]>('/maquinas', { method: 'PUT', body: { maquinas } }),
    onSettled: () => invalidate(cadastroKeys.machines),
  });
}

export function useUpdateTurnos() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (turnos: TurnoAtualizacao[]) =>
      api<RetornoLinha[]>('/turnos', { method: 'PUT', body: { turnos } }),
    onSettled: () => invalidate(cadastroKeys.shifts, cadastroKeys.machines),
  });
}

/**
 * `POST`/`PUT`/`DELETE` em lote de capacidade, largura mãe, tipos de troca e matriz de troca.
 * O corpo é `{ "<chave>": [...] }`, sem `payload` externo (o framework do Datasul já envelopa).
 */
export function useGravarLote(entidade: EntidadeLote) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { metodo: MetodoLote; linhas: unknown[] }) =>
      api<RetornoLinha[]>(`/${entidade}`, { method: v.metodo, body: { [CHAVE_LOTE[entidade]]: v.linhas } }),
    onSettled: () => invalidate(LIST_KEYS[entidade]),
  });
}

export function useGerarCalendario() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (geracoes: GeracaoCalendario[]) =>
      api<ResultadoGeracao[]>('/calendario/gerar', { method: 'POST', body: { geracoes } }),
    onSettled: () => invalidate(['calendario']),
  });
}

export function useAtualizarCalendario() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (dias: DiaCalendarioAtualizacao[]) =>
      api<RetornoLinha[]>('/calendario', { method: 'PUT', body: { dias } }),
    onSettled: () => invalidate(['calendario']),
  });
}
